import { useCallback, useId, useState } from "react";

import { Dialog } from "~/components/Dialog";
import { DialogFooter, DialogHeader, ErrorLine, UserChip } from "~/components/CreateRoomDialog";
import { GhostButton, PrimaryButton } from "~/components/MessageButtons";
import { UserSearch } from "~/components/UserSearch";
import { useMatrix } from "~/context/MatrixContext";
import { getUserProfile, type UserDirectoryEntry } from "~/lib/matrix";

interface NewDmDialogProps {
  onClose: () => void;
}

export function NewDmDialog({ onClose }: NewDmDialogProps) {
  const {
    createDirectRoom,
    setActiveRoomId,
    searchUserDirectory,
    client,
    userId: myUserId,
  } = useMatrix();
  const [people, setPeople] = useState<UserDirectoryEntry[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const titleId = `${useId()}-title`;

  const requestClose = useCallback(() => {
    if (busy) return;
    onClose();
  }, [busy, onClose]);

  const handleSelect = useCallback((user: UserDirectoryEntry) => {
    setPeople((current) =>
      current.some((person) => person.userId === user.userId) ? current : [...current, user],
    );
    setSearchTerm("");
  }, []);

  const handleStart = useCallback(async () => {
    if (people.length === 0 || busy) return;
    setBusy(true);
    setError(null);
    try {
      const userIds = people.map((person) => person.userId);
      const roomId = await createDirectRoom({
        userIds,
      });
      setActiveRoomId(roomId);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't open this conversation. Try again.");
    } finally {
      setBusy(false);
    }
  }, [busy, createDirectRoom, onClose, people, setActiveRoomId]);

  // `myUserId` sits in the disabled list so a user can't try to DM
  // themselves. The stub's directory excludes "You" already, but a
  // live homeserver will return the caller in the result set for
  // any name that matches.
  const disabled = [...(myUserId ? [myUserId] : []), ...people.map((person) => person.userId)];

  return (
    <Dialog onClose={requestClose} labelledBy={titleId}>
      <DialogHeader title="New direct message" titleId={titleId} onClose={requestClose} />
      <div
        style={{
          padding: "var(--chat-dialog-body-padding)",
          display: "flex",
          flexDirection: "column",
          gap: "var(--chat-space-3)",
        }}
      >
        {people.length > 0 && (
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "var(--chat-space-1)",
            }}
          >
            {people.map((person) => (
              <UserChip
                key={person.userId}
                name={person.displayName}
                onRemove={() =>
                  setPeople((current) => current.filter((item) => item.userId !== person.userId))
                }
              />
            ))}
          </div>
        )}
        <UserSearch
          label="Search people"
          placeholder="Search by name or @handle"
          onSearch={searchUserDirectory}
          resolveProfile={client ? (mxid) => getUserProfile(client, mxid) : undefined}
          onSelect={handleSelect}
          disabledUserIds={disabled}
          disabledLabel="Selected"
          value={searchTerm}
          onChangeValue={setSearchTerm}
          autoFocus
        />
        {error && <ErrorLine>{error}</ErrorLine>}
      </div>
      <DialogFooter>
        <GhostButton onClick={requestClose} disabled={busy}>
          Close
        </GhostButton>
        <PrimaryButton onClick={() => void handleStart()} disabled={people.length === 0 || busy}>
          {busy ? "Starting…" : "Start"}
        </PrimaryButton>
      </DialogFooter>
    </Dialog>
  );
}
